/**
 * "Tina Turner already has an open new-hire request" (7 Oct, Rusty's UAT).
 *
 * Shown on "Who is it for?" when the wizard's check finds an open case of the
 * same type for the person, and at Send when the server answers 409. Someone
 * who may open the case gets a link to it; anyone else learns only its
 * number, the name and who asked, and whom to ask. IT and admins may tick
 * "This is a different person with the same name" for a match by name.
 */

import { Alert, Checkbox, Typography } from "@norican/react";
import { Link } from "react-router";

import { useDateFormatter } from "@/components/common";
import { useT } from "@/i18n";

import { accessCasePath, type AccessDuplicateShape, duplicateMessageKey } from "../lib/access";

export interface DuplicateNoticeProps {
  found: AccessDuplicateShape;
  /** False for a Change (28 Oct): the open case is shown, and Send still works. */
  blocking?: boolean;
  /** The reader may tick the override (IT, admins) and the match was by name. */
  canOverride?: boolean;
  override?: boolean;
  onOverride?: (next: boolean) => void;
}

export function DuplicateNotice({
  found,
  blocking = true,
  canOverride = false,
  override = false,
  onOverride,
}: DuplicateNoticeProps) {
  const { t } = useT("tickets");
  const { formatDate } = useDateFormatter();
  const day = (iso: string | null | undefined, noon: boolean) =>
    iso ? formatDate(new Date(noon ? `${iso}T12:00:00` : iso)) : "—";
  const values = {
    name: found.person_name,
    number: found.request_number,
    requester: found.requester_name,
    started: day(found.started_at, false),
    start: day(found.start_date, true),
  };
  const overridable = canOverride && found.by_name === true && onOverride !== undefined;
  const stops = blocking && !(override && overridable);
  return (
    <Alert
      tone={stops ? "warning" : "neutral"}
      title={t(blocking ? "access.duplicate.title" : "access.duplicate.titleOpen")}
      data-form-problem={stops ? "" : undefined}
    >
      <div className="flex flex-col gap-2">
        <Typography as="p" variant="body-sm">
          {t(duplicateMessageKey(found), values)}{" "}
          {found.can_open === true ? (
            <Link
              to={accessCasePath(found.existing_case_id)}
              className="font-medium underline underline-offset-2"
            >
              {t("access.duplicate.openLink")}
            </Link>
          ) : null}
        </Typography>
        {found.can_open === true ? null : (
          <Typography as="p" variant="body-sm">
            {found.contact
              ? t("access.duplicate.askTeam", {
                  requester: found.requester_name,
                  team: found.contact,
                })
              : t("access.duplicate.ask", { requester: found.requester_name })}
          </Typography>
        )}
        {blocking ? null : (
          <Typography as="p" variant="body-sm">
            {t("access.duplicate.stillSend")}
          </Typography>
        )}
        {overridable ? (
          <div className="flex flex-col gap-1">
            <Checkbox.Root
              checked={override}
              onCheckedChange={(next) => onOverride(next === true)}
              label={t("access.duplicate.override")}
            />
            <Typography as="span" variant="caption" tone="muted">
              {t("access.duplicate.overrideHint")}
            </Typography>
          </div>
        ) : null}
      </div>
    </Alert>
  );
}
